import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Zap, TrendingUp, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

const nodes = [
  { key: "strategy", icon: Target, label: "Strategy" },
  { key: "execution", icon: Zap, label: "Execution" },
  { key: "revenue", icon: TrendingUp, label: "Revenue" },
  { key: "data", icon: BarChart3, label: "Data" },
] as const;

// Desktop positions (container 520x520) — node centers
const desktopPositions = [
  { x: 260, y: 40 },   // Strategy — top center
  { x: 440, y: 260 },  // Execution — right center
  { x: 260, y: 470 },  // Revenue — bottom center
  { x: 80, y: 260 },   // Data — left center
];

// Mobile positions (container 340x380)
const mobilePositions = [
  { x: 170, y: 30 },
  { x: 280, y: 190 },
  { x: 170, y: 340 },
  { x: 60, y: 190 },
];

// Bezier curves connecting nodes clockwise: Strategy→Execution→Revenue→Data→Strategy
// Each curve goes from one node toward the next, arcing outward for a circular feel
function getBezierPaths(positions: { x: number; y: number }[]) {
  const cx = positions[0].x; // center x (same as top node x)
  const cy = (positions[1].y + positions[3].y) / 2; // center y

  return [
    // Strategy → Execution (top → right, arc via top-right)
    {
      d: `M ${positions[0].x + 40} ${positions[0].y + 20} C ${positions[0].x + 100} ${positions[0].y + 10}, ${positions[1].x + 10} ${positions[1].y - 100}, ${positions[1].x} ${positions[1].y - 30}`,
      arrowAngle: 80,
      endX: positions[1].x,
      endY: positions[1].y - 30,
    },
    // Execution → Revenue (right → bottom, arc via bottom-right)
    {
      d: `M ${positions[1].x - 10} ${positions[1].y + 30} C ${positions[1].x + 10} ${positions[1].y + 100}, ${positions[2].x + 100} ${positions[2].y - 10}, ${positions[2].x + 40} ${positions[2].y - 15}`,
      arrowAngle: 170,
      endX: positions[2].x + 40,
      endY: positions[2].y - 15,
    },
    // Revenue → Data (bottom → left, arc via bottom-left)
    {
      d: `M ${positions[2].x - 40} ${positions[2].y - 15} C ${positions[2].x - 100} ${positions[2].y - 10}, ${positions[3].x - 10} ${positions[3].y + 100}, ${positions[3].x} ${positions[3].y + 30}`,
      arrowAngle: 260,
      endX: positions[3].x,
      endY: positions[3].y + 30,
    },
    // Data → Strategy (left → top, arc via top-left)
    {
      d: `M ${positions[3].x + 10} ${positions[3].y - 30} C ${positions[3].x - 10} ${positions[3].y - 100}, ${positions[0].x - 100} ${positions[0].y + 10}, ${positions[0].x - 40} ${positions[0].y + 20}`,
      arrowAngle: 350,
      endX: positions[0].x - 40,
      endY: positions[0].y + 20,
    },
  ];
}

export const AlignmentDiagramSection = () => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();

  const positions = isMobile ? mobilePositions : desktopPositions;
  const containerW = isMobile ? 340 : 520;
  const containerH = isMobile ? 380 : 520;
  const nodeW = isMobile ? 120 : 150;
  const paths = getBezierPaths(positions);

  return (
    <section className="py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[600px] rounded-full opacity-10" style={{ background: "var(--gradient-glow)" }} />
      </div>

      <div className="section-container relative z-10">
        {/* Title */}
        <motion.div
          className="text-center mb-16 sm:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("alignment.title")}{" "}
            <span className="text-primary">{t("alignment.titleHighlight")}</span>
          </h2>
          <p className="text-xl sm:text-2xl font-semibold text-primary/80 mb-4">
            {t("alignment.subtitle")}
          </p>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            {t("alignment.description")}
          </p>
        </motion.div>

        {/* Diagram */}
        <div className="flex justify-center mb-14 sm:mb-16">
          <div className="relative" style={{ width: containerW, height: containerH }}>
            {/* SVG arrows */}
            <svg
              viewBox={`0 0 ${containerW} ${containerH}`}
              className="absolute inset-0 w-full h-full"
              fill="none"
            >
              {/* Dashed circle in center */}
              <circle
                cx={containerW / 2}
                cy={containerH / 2}
                r={isMobile ? 60 : 90}
                stroke="hsl(var(--border))"
                strokeWidth="1.5"
                strokeDasharray="8 6"
                opacity="0.4"
              />
              {/* Bezier arrow paths */}
              {paths.map((p, i) => (
                <g key={i}>
                  <motion.path
                    d={p.d}
                    stroke="hsl(var(--primary))"
                    strokeWidth="2"
                    strokeLinecap="round"
                    fill="none"
                    opacity="0.5"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 + i * 0.15 }}
                  />
                  {/* Arrowhead */}
                  <motion.polygon
                    points={(() => {
                      const rad = (p.arrowAngle * Math.PI) / 180;
                      const ax = Math.cos(rad);
                      const ay = Math.sin(rad);
                      const px = -ay;
                      const py = ax;
                      const s = 8;
                      return `${p.endX},${p.endY} ${p.endX - ax * s + px * s * 0.5},${p.endY - ay * s + py * s * 0.5} ${p.endX - ax * s - px * s * 0.5},${p.endY - ay * s - py * s * 0.5}`;
                    })()}
                    fill="hsl(var(--primary))"
                    opacity="0.5"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 0.5 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.8 + i * 0.15 }}
                  />
                </g>
              ))}
            </svg>

            {/* Center label */}
            <motion.div
              className="absolute flex items-center justify-center"
              style={{
                left: containerW / 2 - (isMobile ? 50 : 68),
                top: containerH / 2 - (isMobile ? 50 : 68),
                width: isMobile ? 100 : 136,
                height: isMobile ? 100 : 136,
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="w-full h-full rounded-full border border-primary/30 bg-card/80 backdrop-blur-sm flex flex-col items-center justify-center text-center p-2">
                <span className="text-primary font-bold text-xs sm:text-sm leading-tight">
                  {t("alignment.center")}
                </span>
                <span className="text-muted-foreground text-[9px] sm:text-xs mt-1 leading-tight">
                  {t("alignment.centerSub")}
                </span>
              </div>
            </motion.div>

            {/* Nodes */}
            {nodes.map((node, i) => (
              <motion.div
                key={node.key}
                className="absolute"
                style={{
                  left: positions[i].x - nodeW / 2,
                  top: positions[i].y - (isMobile ? 28 : 35),
                  width: nodeW,
                }}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.12 }}
              >
                <div className="card-surface p-3 sm:p-4 flex flex-col items-center text-center">
                  <div className="icon-box-sm mb-2">
                    <node.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <span className="font-semibold text-foreground text-xs sm:text-sm">
                    {t(`alignment.${node.key}` as any)}
                  </span>
                  <span className="text-muted-foreground text-[10px] sm:text-xs mt-1 leading-tight">
                    {t(`alignment.${node.key}Desc` as any)}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Link to="/contact">
            <Button size="lg" className="group">
              {t("alignment.cta")}
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
